import { mediaList } from "@/mocks/media";
import type { MediaItem, SortKey } from "@/types/media";

export const getMediaById = (id?: string): MediaItem | undefined =>
  id ? mediaList.find((item) => item.id === id) : undefined;

export const getFeatured = (limit = 5): MediaItem[] =>
  mediaList.filter((item) => item.featured).slice(0, limit);

export const getByType = (type: "movie" | "tv"): MediaItem[] =>
  mediaList.filter((item) => item.type === type);

export const getLatest = (type?: "movie" | "tv", limit = 6): MediaItem[] =>
  [...(type ? getByType(type) : mediaList)]
    .sort((a, b) => b.year - a.year || b.views - a.views)
    .slice(0, limit);

export const getHot = (limit = 6): MediaItem[] =>
  [...mediaList].sort((a, b) => b.views - a.views).slice(0, limit);

export const getAllGenres = (type?: "movie" | "tv"): string[] => {
  const source = type ? getByType(type) : mediaList;
  const set = new Set<string>();
  source.forEach((item) => item.genres.forEach((genre) => set.add(genre)));
  return Array.from(set);
};

export const getAllRegions = (type?: "movie" | "tv"): string[] => {
  const source = type ? getByType(type) : mediaList;
  return Array.from(new Set(source.map((item) => item.region)));
};

export const getYearBuckets = (type?: "movie" | "tv"): { label: string; test: (year: number) => boolean }[] => {
  const source = type ? getByType(type) : mediaList;
  const years = source.map((item) => item.year);
  const min = Math.min(...years);
  const max = Math.max(...years);
  const buckets: { label: string; test: (year: number) => boolean }[] = [];
  const mid = Math.ceil((min + max) / 2);
  buckets.push({ label: `${min}-${mid - 1}`, test: (year) => year >= min && year < mid });
  buckets.push({ label: `${mid}-${max}`, test: (year) => year >= mid && year <= max });
  return buckets;
};

export interface FilterOptions {
  type?: "movie" | "tv";
  genres?: string[];
  region?: string;
  yearTest?: (year: number) => boolean;
  keyword?: string;
}

export const filterMedia = (options: FilterOptions): MediaItem[] => {
  const { type, genres = [], region, yearTest, keyword } = options;
  return mediaList.filter((item) => {
    if (type && item.type !== type) return false;
    if (genres.length > 0 && !genres.some((genre) => item.genres.includes(genre))) return false;
    if (region && item.region !== region) return false;
    if (yearTest && !yearTest(item.year)) return false;
    if (keyword) {
      const kw = keyword.trim().toLowerCase();
      if (kw && !item.title.toLowerCase().includes(kw)) return false;
    }
    return true;
  });
};

export const sortMedia = (list: MediaItem[], sortKey: SortKey): MediaItem[] => {
  const sorted = [...list];
  if (sortKey === "rating") sorted.sort((a, b) => b.rating - a.rating);
  else if (sortKey === "hot") sorted.sort((a, b) => b.views - a.views);
  else sorted.sort((a, b) => b.year - a.year || b.views - a.views);
  return sorted;
};

export const getRecommendations = (item: MediaItem, limit = 4): MediaItem[] => {
  return mediaList
    .filter((candidate) => candidate.id !== item.id)
    .map((candidate) => {
      let score = 0;
      score += candidate.genres.filter((genre) => item.genres.includes(genre)).length * 3;
      if (candidate.director === item.director) score += 4;
      score += candidate.cast.filter((actor) => item.cast.includes(actor)).length * 2;
      if (candidate.type === item.type) score += 1;
      return { candidate, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.candidate.views - a.candidate.views)
    .slice(0, limit)
    .map((entry) => entry.candidate);
};

export const searchMedia = (keyword: string): MediaItem[] => {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return mediaList.filter((item) => item.title.toLowerCase().includes(kw));
};

export const formatViews = (views: number): string => {
  if (views >= 10000) return `${(views / 10000).toFixed(1)} 亿`;
  return `${views} 万`;
};